import { useCallback, useEffect, useState } from 'react'
import type { Incident, WorkOrder } from '@/api/types'
import { isUsingMocks, repository, type Tracks } from '@/api/repository'
import { messageFor } from '@/api/errors'
import { useIncidents } from '@/hooks/useIncidents'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { useTheme } from '@/hooks/useTheme'
import { Icon } from '@/components/Icons'
import { IncidentList } from '@/components/IncidentList'
import { IncidentDetail } from '@/components/IncidentDetail'
import { WorkOrderList } from '@/components/WorkOrderList'
import { WorkOrderDetail } from '@/components/WorkOrderDetail'
import { EmptyIncidents, ErrorState } from '@/components/atoms'

function ThemeButton() {
  const { dark, toggle } = useTheme()
  const label = dark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'
  return (
    <button
      type="button"
      className="theme-btn"
      onClick={toggle}
      aria-pressed={dark}
      aria-label={label}
      title={label}
    >
      <Icon name={dark ? 'sun' : 'moon'} size={17} />
    </button>
  )
}

export default function App() {
  const [tracks, setTracks] = useState<Tracks | null>(null)
  const [tracksError, setTracksError] = useState<string | null>(null)

  const incidents = useIncidents()

  /*
   * Se guarda el ID de lo seleccionado, no el objeto.
   *
   * La tarjeta del listado y la sección de detalle salen así del MISMO
   * elemento, con lo que cualquier relectura —el botón de actualizar, o la que
   * dispara elegir una tarjeta— las pone al día a las dos a la vez. Guardando
   * el objeto, el listado se actualizaba y el detalle se quedaba mostrando la
   * copia vieja que se había capturado al hacer clic.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedOtId, setSelectedOtId] = useState<string | null>(null)

  const selected = incidents.items.find((i) => i._id === selectedId) ?? null
  const workOrders = useWorkOrders(selected)
  const selectedOt = workOrders.items.find((o) => o._id === selectedOtId) ?? null

  // Estables entre renders; se sacan del objeto para no rehacer los callbacks.
  const { refreshOne: refreshIncident } = incidents
  const { refresh: refreshWorkOrders } = workOrders

  const INCIDENT_SECTION = 'sec-incidencia'
  const OT_SECTION = 'sec-ot-detalle'

  useEffect(() => {
    let alive = true
    repository
      .loadTracks()
      .then((t) => alive && setTracks(t))
      .catch((err: unknown) => {
        if (alive) setTracksError(messageFor(err, 'el catálogo de estados'))
      })
    return () => {
      alive = false
    }
  }, [])

  const scrollTo = useCallback((id: string) => {
    // Solo en una columna: desde 1024 px las secciones ya están a la vista.
    if (window.matchMedia('(min-width: 1024px)').matches) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // En el mismo frame la sección puede no existir todavía.
    requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    })
  }, [])

  // En dos columnas la derecha nace vacía y se lee como si algo hubiera
  // fallado, así que se abre la primera incidencia —la primera en el orden que
  // se ve: primer grupo, más prioritaria—. Su grupo sigue contraído; la barra
  // lo marca. En una columna no: ahí el listado es la pantalla y elegir es del
  // usuario.
  useEffect(() => {
    if (selected || incidents.groups.length === 0) return
    if (!window.matchMedia('(min-width: 1024px)').matches) return
    setSelectedId(incidents.groups[0]?.incidents[0]?._id ?? null)
  }, [incidents.groups, selected])

  /*
   * Elegir algo lo relee de la API.
   *
   * La selección se fija de inmediato y la relectura va detrás: pintar con lo
   * que ya se tiene y reemplazarlo cuando llega lo fresco es preferible a dejar
   * la sección vacía durante la ida y vuelta, que se lee como si algo hubiera
   * fallado. Como la vista se deriva del listado, el dato nuevo aparece solo en
   * la tarjeta y en el detalle.
   */
  const selectIncident = useCallback(
    (incident: Incident) => {
      setSelectedId(incident._id)
      setSelectedOtId(null) // limpia la OT y colapsa la sección 4 (spec §6.4)
      scrollTo(INCIDENT_SECTION)
      void refreshIncident(incident._id)
    },
    [scrollTo, refreshIncident],
  )

  const selectWorkOrder = useCallback(
    (ot: WorkOrder) => {
      setSelectedOtId(ot._id)
      scrollTo(OT_SECTION)
      refreshWorkOrders()
    },
    [scrollTo, refreshWorkOrders],
  )

  if (tracksError) {
    return (
      <div className="app">
        <div className="app-bar">
          <h1>Mis incidencias</h1>
          <ThemeButton />
        </div>
        <div className="app-body">
          <div className="sec-in" style={{ paddingTop: 18 }}>
            <ErrorState message={tracksError} onRetry={() => window.location.reload()} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      <div className="app-bar">
        <h1>Mis incidencias</h1>
        {isUsingMocks() && (
          <span
            className="pill pill--amber"
            title="USE_MOCKS está activo: los datos son de muestra, no vienen de Cotalker"
          >
            <span className="dot" />
            Datos de muestra
          </span>
        )}
        <ThemeButton />
      </div>

      <div className="app-body">
        {tracks && (
          <IncidentList
            groups={incidents.groups}
            truncated={incidents.truncated}
            track={tracks.incidents}
            selectedId={selected?._id ?? null}
            onSelect={selectIncident}
            loading={incidents.loading}
            refreshing={incidents.refreshing}
            onRefresh={incidents.refresh}
          />
        )}

        <div className="detail-col">
          {incidents.error && incidents.items.length === 0 ? (
            <div className="sec-in" style={{ paddingTop: 18 }}>
              <ErrorState message={incidents.error} onRetry={incidents.retry} />
            </div>
          ) : !incidents.initial && incidents.items.length === 0 ? (
            <div className="sec-in" style={{ paddingTop: 18 }}>
              <EmptyIncidents />
            </div>
          ) : (
            tracks && (
              <>
                <IncidentDetail
                  id={INCIDENT_SECTION}
                  incident={selected}
                  track={tracks.incidents}
                />

                {selected && (
                  <WorkOrderList
                    incident={selected}
                    workOrders={workOrders.items}
                    loading={workOrders.loading}
                    refreshing={workOrders.refreshing}
                    error={workOrders.error}
                    track={tracks.workOrders}
                    selectedId={selectedOt?._id ?? null}
                    onSelect={selectWorkOrder}
                    onRefresh={refreshWorkOrders}
                  />
                )}

                {selected && workOrders.items.length > 0 && (
                  <WorkOrderDetail
                    id={OT_SECTION}
                    workOrder={selectedOt}
                    track={tracks.workOrders}
                  />
                )}
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}
